import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';

interface SortingState {
    [pageId: string]: {
        key: string;
        direction: 'ascending' | 'descending' | null;
    };
}

const initialState: SortingState = {};

const sortingSlice = createSlice({
    name: 'sorting',
    initialState,
    reducers: {
        setSort: (state, action: PayloadAction<{ pageId: string; key: string; direction?: 'ascending' | 'descending' }>) => {
            const { pageId, key, direction } = action.payload;
            if (!state[pageId] || state[pageId].key !== key) {
                state[pageId] = { key, direction: direction || 'ascending' };
            } else {
                state[pageId].direction = state[pageId].direction === 'ascending' ? 'descending' : 'ascending';
            }
        },
    },
});

export const { setSort } = sortingSlice.actions;

export const selectSorting = (state: RootState, pageId: string) =>
    state.sorting[pageId] || { key: '', direction: null };

// Helper function to get nested property value using path string like "brand.brand"
const getNestedValue = (obj: any, path: string) => {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((prev, curr) => (prev ? prev[curr] : undefined), obj);
};

// Helper function to check if a string is a valid date in YYYY-MM-DD format
const isDateString = (value: string): boolean => {
    if (!value || typeof value !== 'string') return false;
    return /^\d{4}-\d{2}-\d{2}$/.test(value);
};

export const selectSortedData = createSelector(
    [selectSorting, (state: RootState, pageId: string, data: any[]) => data],
    (sorting, data) => {
        if (!sorting.key || !data) return data;

        return [...data].sort((a, b) => {
            const aValue = getNestedValue(a, sorting.key);
            const bValue = getNestedValue(b, sorting.key);

            // --- Handle empty values: push them to the end ---
            const isAEmpty = aValue === undefined || aValue === null || aValue === '';
            const isBEmpty = bValue === undefined || bValue === null || bValue === '';
            if (isAEmpty && !isBEmpty) return 1;
            if (!isAEmpty && isBEmpty) return -1;
            if (isAEmpty && isBEmpty) return 0;

            // --- Handle date comparison using milliseconds ---
            if (isDateString(aValue) && isDateString(bValue)) {
                const timeA = new Date(aValue).getTime();
                const timeB = new Date(bValue).getTime();

                if (timeA < timeB) return sorting.direction === 'ascending' ? -1 : 1;
                if (timeA > timeB) return sorting.direction === 'ascending' ? 1 : -1;
                return 0;
            }

            // --- Handle numeric strings as numbers ---
            const numA = Number(aValue);
            const numB = Number(bValue);
            const aIsNum = !isNaN(numA);
            const bIsNum = !isNaN(numB);

            if (aIsNum && bIsNum) {
                if (numA < numB) return sorting.direction === 'ascending' ? -1 : 1;
                if (numA > numB) return sorting.direction === 'ascending' ? 1 : -1;
                return 0;
            }

            // --- Regular string comparison ---
            if (aValue < bValue) return sorting.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sorting.direction === 'ascending' ? 1 : -1;
            return 0;
        });
    }
);

export default sortingSlice.reducer;